// @symbo.ls/app-rules audit — the AST gate for AI-generated app pages.
//
// The rules are data in rules.js (no imports; every consumer reads the same lists).
// This module parses the EXACT text the freestyle parser evaluates (acorn — a
// runtime dependency of the shared package already, no regex) and refuses:
//   • any free identifier outside GENERATED_APP_GLOBALS (window, self, top,
//     frames, opener, document, fetch, Function, eval, … fall out by
//     construction), resolved per lexical scope so a local `let self` in one
//     function never hides a global `self` in another; `arguments` always;
//   • GENERATED_APP_BLOCKED_MEMBERS as a member, a destructured key or a
//     literal computed key, any `__`-prefixed member, `.apply` / `.bind`, and
//     a computed member key that is not a literal;
//   • `.call` anywhere except `x.call('<allowlisted literal>', …)`;
//   • on an EVENT handle (first parameter of a DOMQL `on<Event>` handler) any
//     member outside GENERATED_APP_EVENT_MEMBERS (`e.view` is the window), and
//     on `e.target` any member outside GENERATED_APP_TARGET_MEMBERS;
//   • on an ELEMENT handle (`el`, `this`) any member outside
//     GENERATED_APP_ELEMENT_MEMBERS — in-shell every shell method is a
//     property of every element, `el.sdk` included;
//   • a handle that escapes: an event / element / `this` may only be the object
//     of a member read or an argument to an allowlisted `el.call`;
//   • `x.root` not followed by a key that passes the seed deny list, and any
//     write (assignment, update, delete, state write method) under a `.root`
//     chain — in-shell `s.root` is the LIVE workspace root;
//   • reflective members of globals (Object.getPrototypeOf, …) and string
//     literals naming GENERATED_APP_BLOCKED_STRINGS;
//   • `with`, tagged templates, `import()`, `import.meta`, `new.target`,
//     setTimeout / setInterval with anything but a function;
//   • and the same inside any string that parses as a function (a
//     JSON-authored source carries its handlers as strings that the parser
//     destringifies and evaluates).
import { parse } from 'acorn'
import * as appRules from './generatedAppRules.js'

const BLOCKED_MEMBERS = new Set(appRules.GENERATED_APP_BLOCKED_MEMBERS)
const BLOCKED_STRINGS = new Set(appRules.GENERATED_APP_BLOCKED_STRINGS)
const INDIRECTION = new Set(appRules.GENERATED_APP_BLOCKED_INDIRECTION)
const GLOBALS = new Set(appRules.GENERATED_APP_GLOBALS)
const EVENT_MEMBERS = new Set(appRules.GENERATED_APP_EVENT_MEMBERS)
const TARGET_MEMBERS = new Set(appRules.GENERATED_APP_TARGET_MEMBERS)
const TARGET_DEEP = new Set(appRules.GENERATED_APP_TARGET_DEEP_MEMBERS)
const ELEMENT_MEMBERS = new Set(appRules.GENERATED_APP_ELEMENT_MEMBERS)
const LIFECYCLE = new Set(appRules.GENERATED_APP_LIFECYCLE_HANDLERS)
const ROOT_DENY = new Set(appRules.GENERATED_APP_ROOT_DENY_KEYS)
const ROOT_DENY_RE = new RegExp(appRules.GENERATED_APP_ROOT_DENY_PATTERN, 'i')
const ROOT_WRITES = new Set(appRules.GENERATED_APP_ROOT_WRITE_METHODS)
const GLOBAL_MEMBERS = appRules.GENERATED_APP_GLOBAL_MEMBERS
const TAGS = new Set(appRules.GENERATED_APP_TAG_ALLOWLIST)
const COMPONENT_ALLOW = new Set(appRules.GENERATED_APP_COMPONENT_ALLOWLIST)
const COMPONENT_DENY = new Set(appRules.GENERATED_APP_COMPONENT_DENYLIST)
const MARKUP_KEYS = new Set(appRules.GENERATED_APP_MARKUP_KEYS)
const URL_KEYS = new Set(appRules.GENERATED_APP_URL_KEYS)
const URL_SCHEMES = new Set(appRules.GENERATED_APP_URL_SCHEMES)
const CSS_URL_KEYS = new Set(appRules.GENERATED_APP_CSS_URL_KEYS)
const DEEP_CHAIN = appRules.GENERATED_APP_TARGET_DEEP_CHAIN
const EVENT_VALUE = new Set(appRules.GENERATED_APP_EVENT_VALUE_MEMBERS)
const TARGET_VALUE = new Set(appRules.GENERATED_APP_TARGET_VALUE_MEMBERS)

// A URL string is safe when, with whitespace and control characters removed
// (browsers ignore them in a scheme), it has no scheme (relative) or one of
// GENERATED_APP_URL_SCHEMES; `src` may also be an inline image.
const urlSchemeOf = (str) => {
  const clean = String(str).replace(/[\u0000-\u0020\u007f-\u009f]/g, '')
  const m = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(clean)
  return { clean, scheme: m ? m[1].toLowerCase() : null }
}
const isSafeUrl = (str, key) => {
  const { clean, scheme } = urlSchemeOf(str)
  if (!scheme) return true
  if (URL_SCHEMES.has(scheme)) return true
  return key === 'src' && clean.toLowerCase().startsWith(appRules.GENERATED_APP_SRC_DATA_PREFIX)
}
// A leading literal fixes the whole ORIGIN when it is a SAME-ORIGIN PATH: one
// '/' followed by a character that is neither '/' nor a backslash (both open a
// host — a browser reads '/\evil.example' as '//evil.example'), or a '?' / '#' that
// keeps the address on this page. Nothing appended after such a prefix can
// introduce a scheme or a host. A prefix that carries a SCHEME is not enough:
// 'https://example.com/' + id fixes the scheme and leaves the host to the
// expression, so it stays refused.
const sameOriginPathPrefix = (prefix) => {
  const { clean, scheme } = urlSchemeOf(prefix)
  if (scheme) return false
  return /^(?:\/[^/\\]|[?#])/.test(clean)
}
// The leftmost part of an address built at run time: the first operand of a
// `+` chain, or the first quasi of a template literal. `null` when the left
// end is itself an expression (`${base}/x`, `base + '/x'`).
const leftmostLiteral = (v) => {
  let n = v
  while (n && n.type === 'BinaryExpression' && n.operator === '+') n = n.left
  if (!n) return null
  if (n.type === 'TemplateLiteral') return n.quasis[0] ? n.quasis[0].value.cooked : null
  return literalString(n)
}

export const GENERATED_APP_BLOCKED = 'generated_app_blocked'

const isFnNode = (n) =>
  !!n &&
  (n.type === 'ArrowFunctionExpression' ||
    n.type === 'FunctionExpression' ||
    n.type === 'FunctionDeclaration')

const literalString = (n) => {
  if (!n) return null
  if (n.type === 'Literal' && (typeof n.value === 'string' || typeof n.value === 'number'))
    return String(n.value)
  if (n.type === 'TemplateLiteral' && n.expressions.length === 0)
    return n.quasis.map((q) => q.value.cooked).join('')
  return null
}

const propName = (prop) => {
  if (!prop) return null
  if (prop.computed) return literalString(prop.key)
  if (prop.key.type === 'Identifier') return prop.key.name
  return literalString(prop.key)
}

const memberName = (m) => {
  if (!m.computed)
    return m.property.type === 'PrivateIdentifier' ? '#' + m.property.name : m.property.name
  return literalString(m.property)
}

const looksLikeFunctionText = (s) => /=>|^\s*(async\s+)?function\b/.test(s)
const parsesAsFunction = (s) => {
  try {
    const e = parse('(' + s + ')', { ecmaVersion: 'latest', sourceType: 'script' }).body[0]
    return !!e && isFnNode(e.expression)
  } catch (_) {
    return false
  }
}

// Role of each parameter of a function that is the value of DOMQL key `key`.
// Functions under these keys take their own arguments (the app's own
// `functions`, a `scope` helper, a `fetch` transform …): their parameters are
// plain values. `this` stays an element and the member denylist still applies.
const PLAIN_FN_KEYS = new Set(['functions', 'methods', 'scope', 'fetch'])

const paramRoles = (key) => {
  if (typeof key === 'string' && /^on[A-Z]/.test(key) && !LIFECYCLE.has(key))
    return ['event', 'element', 'state']
  if (typeof key === 'string') return ['element', 'state']
  return []
}

export function auditGeneratedAppExpression(
  text,
  { allow = appRules.APP_EL_CALL_ALLOWLIST, rootKeys = [], registeredComponents = null } = {}
) {
  const violations = []
  const allowSet = new Set(allow)
  const flag = (rule, node, detail) => {
    violations.push({
      rule,
      at: node && typeof node.start === 'number' ? node.start : null,
      detail
    })
  }

  let ast
  try {
    ast = parse(String(text || ''), { ecmaVersion: 'latest', sourceType: 'script' })
  } catch (e) {
    return {
      ok: false,
      violations: [{ rule: 'parse', at: null, detail: (e && e.message) || String(e) }]
    }
  }

  // `s.root.<key>`: the readable keys are an Array or a Set (the active
  // module's catalog stateKeys + loader write keys) plus the `fetch` `as`
  // targets this source declares itself. ANY other value — undefined, null, a
  // string, an object — is [] (every root read refused); there is no
  // deny-list-only mode (core-wl review of 1ecd5e8b6).
  const rootAllow = new Set(Array.isArray(rootKeys) || rootKeys instanceof Set ? [...rootKeys] : [])
  const collectAs = (n) => {
    if (!n || typeof n !== 'object') return
    if (Array.isArray(n)) return n.forEach(collectAs)
    if (n.type === 'Property' && propName(n) === 'as') {
      const v = literalString(n.value)
      if (v) rootAllow.add(v.replace(/^~?\/?/, ''))
    }
    for (const k in n) if (k !== 'type' && n[k] && typeof n[k] === 'object') collectAs(n[k])
  }
  collectAs(ast)

  // Components: the allowlist + the app's own `components:` names when no
  // registered or denied component carries the name (the supplementary
  // registry never shadows one, so the app's def would not be what renders).
  const registered = Array.isArray(registeredComponents) ? new Set(registeredComponents) : null
  const ownComponents = new Set()
  const collectOwn = (n) => {
    if (!n || typeof n !== 'object') return
    if (Array.isArray(n)) return n.forEach(collectOwn)
    if (
      n.type === 'Property' &&
      propName(n) === 'components' &&
      n.value &&
      n.value.type === 'ObjectExpression'
    ) {
      for (const q of n.value.properties) {
        const name = q.type === 'Property' ? propName(q) : null
        if (name && !COMPONENT_DENY.has(name) && !(registered && registered.has(name)))
          ownComponents.add(name)
      }
    }
    for (const k in n) if (k !== 'type' && n[k] && typeof n[k] === 'object') collectOwn(n[k])
  }
  collectOwn(ast)
  const componentAllowed = (name) => COMPONENT_ALLOW.has(name) || ownComponents.has(name)
  const checkExtendsValue = (v, node) => {
    const names = []
    if (v && v.type === 'ArrayExpression') {
      for (const e of v.elements) {
        const lit = literalString(e)
        if (lit == null) return flag('component_not_allowed', node, '(computed)')
        names.push(lit)
      }
    } else {
      const lit = literalString(v)
      if (lit == null) return flag('component_not_allowed', node, '(computed)')
      names.push(lit)
    }
    for (const n of names) {
      const base = n.split('>')[0].trim()
      if (!componentAllowed(base)) flag('component_not_allowed', node, base)
    }
  }
  // A bare PascalCase child key resolves to a registered component of that
  // name (the part before `_`): refused when the name is denied, or, with
  // the live registry, registered and off the list. Other keys are labels.
  const checkBareKey = (key, node) => {
    if (!/^[A-Z]/.test(key)) return
    const base = key.split('_')[0]
    if (componentAllowed(base)) return
    if (COMPONENT_DENY.has(base) || (registered && registered.has(base))) {
      flag('component_not_allowed', node, base)
    }
  }

  // ── scopes ──────────────────────────────────────────────────────────────
  const newScope = (parent) => ({ parent, names: new Map() })
  const resolve = (scope, name) => {
    for (let s = scope; s; s = s.parent)
      if (s.names.has(name)) return { declared: true, role: s.names.get(name) }
    return { declared: false, role: null }
  }
  const patternNames = (p, out = []) => {
    if (!p) return out
    if (p.type === 'Identifier') out.push(p.name)
    else if (p.type === 'ObjectPattern')
      p.properties.forEach((q) =>
        patternNames(q.type === 'RestElement' ? q.argument : q.value, out)
      )
    else if (p.type === 'ArrayPattern') p.elements.forEach((q) => patternNames(q, out))
    else if (p.type === 'RestElement') patternNames(p.argument, out)
    else if (p.type === 'AssignmentPattern') patternNames(p.left, out)
    return out
  }
  // Block-level declarations of a statement list (let/const/class/function).
  const declareBlock = (scope, body) => {
    for (const st of body || []) {
      if (st.type === 'VariableDeclaration' && st.kind !== 'var') {
        st.declarations.forEach((d) =>
          patternNames(d.id).forEach((n) => scope.names.set(n, 'plain'))
        )
      } else if ((st.type === 'ClassDeclaration' || st.type === 'FunctionDeclaration') && st.id) {
        scope.names.set(st.id.name, 'plain')
      }
    }
  }
  // `var` declarations hoist to the function scope (not into nested functions).
  const declareVars = (scope, node) => {
    if (!node || typeof node !== 'object') return
    if (Array.isArray(node)) return node.forEach((n) => declareVars(scope, n))
    if (isFnNode(node) || node.type === 'ClassBody') return
    if (node.type === 'VariableDeclaration' && node.kind === 'var') {
      node.declarations.forEach((d) =>
        patternNames(d.id).forEach((n) => scope.names.set(n, 'plain'))
      )
    }
    for (const k in node) {
      if (k === 'type' || k === 'start' || k === 'end') continue
      const v = node[k]
      if (v && typeof v === 'object') declareVars(scope, v)
    }
  }

  // ── member chains ───────────────────────────────────────────────────────
  // a.b.c → { base: a, segs: [{ name, node }, …] } (stops at a call / other node)
  const chainOf = (node) => {
    const segs = []
    let n = node
    while (n.type === 'MemberExpression' || n.type === 'ChainExpression') {
      if (n.type === 'ChainExpression') {
        n = n.expression
        continue
      }
      segs.unshift({ name: memberName(n), node: n })
      n = n.object
    }
    return { base: n, segs }
  }

  const handleRole = (base, scope) => {
    if (!base) return null
    if (base.type === 'ThisExpression') return 'element'
    if (base.type === 'Identifier') {
      const r = resolve(scope, base.name)
      if (r.declared && (r.role === 'event' || r.role === 'element' || r.role === 'state'))
        return r.role
    }
    return null
  }

  const checkChain = (node, scope, { isCallee = false, isWrite = false } = {}) => {
    const { base, segs } = chainOf(node)
    const role = handleRole(base, scope)
    // the base: a handle is fine as a member object; anything else is visited
    if (base.type === 'Identifier') checkIdentifierRef(base, scope, 'member-object')
    else if (base.type !== 'ThisExpression') visit(base, scope)

    for (let i = 0; i < segs.length; i++) {
      const { name, node: m } = segs[i]
      if (m.computed && name == null) {
        flag('computed-key', m, 'a member key computed from an expression')
        visit(m.property, scope)
        continue
      }
      if (m.computed) visit(m.property, scope)
      if (name == null) continue
      if (BLOCKED_MEMBERS.has(name)) flag('blocked-member', m, name)
      else if (name.startsWith(appRules.GENERATED_APP_BLOCKED_PREFIX)) flag('internal-member', m, name)
      else if (INDIRECTION.has(name)) flag('indirection', m, name)
      else if (name === 'call' && !(isCallee && i === segs.length - 1))
        flag('call-indirection', m, 'call')
      if (BLOCKED_STRINGS.has(name) && !BLOCKED_MEMBERS.has(name)) flag('blocked-member', m, name)

      // roles
      if (i === 0 && role === 'event' && !EVENT_MEMBERS.has(name)) flag('event-member', m, name)
      if (i === 0 && role === 'element' && !ELEMENT_MEMBERS.has(name))
        flag('element-member', m, name)
      if (
        i === 1 &&
        role === 'event' &&
        (segs[0].name === 'target' || segs[0].name === 'currentTarget') &&
        !TARGET_MEMBERS.has(name)
      ) {
        flag('target-member', m, name)
      }
      if (
        i >= 2 &&
        role === 'event' &&
        (segs[0].name === 'target' || segs[0].name === 'currentTarget')
      ) {
        const deep = segs[1].name
        const chain = DEEP_CHAIN[deep]
        const isIndex = /^\d+$/.test(name)
        let okDeep = false
        if (deep === 'dataset') okDeep = i === 2
        else if (chain) {
          if (i === 2) okDeep = isIndex || chain.list.includes(name)
          else if (i === 3) okDeep = /^\d+$/.test(segs[2].name || '') && chain.item.includes(name)
        }
        if (!okDeep) flag('target-member', m, name)
      }
      // globals
      if (
        i === 0 &&
        base.type === 'Identifier' &&
        !resolve(scope, base.name).declared &&
        GLOBAL_MEMBERS[base.name]
      ) {
        if (!GLOBAL_MEMBERS[base.name].includes(name))
          flag('global-member', m, `${base.name}.${name}`)
      }
      // root
      if (name === 'root') {
        const next = segs[i + 1]
        if (!next || next.name == null) flag('root-read', m, 'root must be followed by a data key')
        else if (ROOT_DENY.has(next.name) || ROOT_DENY_RE.test(next.name))
          flag('root-key', next.node, next.name)
        else if (!rootAllow.has(next.name)) flag('root-key', next.node, next.name)
        for (let j = i + 1; j < segs.length; j++) {
          if (segs[j].name && ROOT_WRITES.has(segs[j].name))
            flag('root-write', segs[j].node, segs[j].name)
        }
        if (isWrite) flag('root-write', m, 'assignment under root')
      }
      if (i === segs.length - 1 && isWrite && (role === 'event' || role === 'element')) {
        flag('handle-write', m, name)
      }
    }
  }

  // A handle chain used as a VALUE must end at a primitive member.
  const checkHandleValueEnd = (node, scope) => {
    const { base, segs } = chainOf(node)
    const role = handleRole(base, scope)
    if (role !== 'event' || !segs.length) return
    const names = segs.map((x) => x.name)
    const onTarget = names[0] === 'target' || names[0] === 'currentTarget'
    let ok
    if (!onTarget) ok = names.length === 1 && EVENT_VALUE.has(names[0])
    else if (names.length === 1) ok = false
    else if (names.length === 2) ok = TARGET_VALUE.has(names[1])
    else if (names[1] === 'dataset') ok = names.length === 3
    else if (names[1] === 'files') ok = true
    else if (names[1] === 'options')
      ok = names.length === 4 || (names.length === 3 && !/^\d+$/.test(names[2]))
    else ok = false
    if (!ok) flag('handle-escape', node, names.join('.'))
  }

  const checkIdentifierRef = (id, scope, position) => {
    if (id.name === 'arguments') return flag('arguments', id, 'arguments')
    const r = resolve(scope, id.name)
    if (!r.declared) {
      if (!GLOBALS.has(id.name)) flag('global', id, id.name)
      return
    }
    if (
      (r.role === 'event' || r.role === 'element') &&
      position !== 'member-object' &&
      position !== 'allowed-arg'
    ) {
      flag('handle-escape', id, id.name)
    }
  }

  const checkPatternKeys = (p, scope, role) => {
    if (!p) return
    if (p.type === 'ObjectPattern') {
      for (const q of p.properties) {
        if (q.type === 'RestElement') {
          if (role === 'event' || role === 'element') flag('handle-escape', q, 'rest of a handle')
          checkPatternKeys(q.argument, scope, null)
          continue
        }
        const name = propName(q)
        if (q.computed && name == null) {
          flag('computed-key', q, 'a destructured key computed from an expression')
          visit(q.key, scope)
        }
        if (name != null) {
          if (BLOCKED_MEMBERS.has(name) || BLOCKED_STRINGS.has(name))
            flag('blocked-member', q, name)
          else if (name.startsWith(appRules.GENERATED_APP_BLOCKED_PREFIX)) flag('internal-member', q, name)
          if (role === 'event' && !EVENT_MEMBERS.has(name)) flag('event-member', q, name)
          if (role === 'element' && !ELEMENT_MEMBERS.has(name)) flag('element-member', q, name)
          if (name === 'root') flag('root-read', q, 'root must be read as root.<key>')
        }
        if (q.value && q.value.type !== 'Identifier') checkPatternKeys(q.value, scope, null)
      }
    } else if (p.type === 'ArrayPattern') {
      if (role === 'event' || role === 'element')
        flag('handle-escape', p, 'array pattern of a handle')
      p.elements.forEach((q) => checkPatternKeys(q, scope, null))
    } else if (p.type === 'AssignmentPattern') {
      checkPatternKeys(p.left, scope, role)
      visit(p.right, scope)
    } else if (p.type === 'RestElement') {
      checkPatternKeys(p.argument, scope, null)
    }
  }

  const enterFunction = (fn, scope, key) => {
    const s = newScope(scope)
    const roles = paramRoles(key)
    fn.params.forEach((p, i) => {
      const role = roles[i] || 'plain'
      if (p.type === 'Identifier') s.names.set(p.name, role)
      else {
        patternNames(p).forEach((n) => s.names.set(n, 'plain'))
        checkPatternKeys(p, s, role)
      }
    })
    if (fn.type === 'FunctionExpression' && fn.id) s.names.set(fn.id.name, 'plain')
    if (fn.body.type === 'BlockStatement') {
      declareVars(s, fn.body)
      declareBlock(s, fn.body.body)
      fn.body.body.forEach((st) => visit(st, s))
    } else {
      visit(fn.body, s)
    }
  }

  const auditStringFunction = (str, node, key) => {
    let inner
    try {
      inner = parse('(' + str + ')', { ecmaVersion: 'latest', sourceType: 'script' })
    } catch (_) {
      return
    }
    const expr = inner.body[0] && inner.body[0].expression
    if (!isFnNode(expr)) return
    const sub = auditGeneratedAppExpression('(' + str + ')', { allow: [...allowSet], rootKeys })
    // a role-aware pass for the DOMQL key it sits on
    const s = newScope(null)
    enterFunctionStandalone(expr, s, key, sub.violations, node)
  }
  const enterFunctionStandalone = (expr, s, key, subViolations, node) => {
    for (const v of subViolations) flag(v.rule, node, `in a string function: ${v.detail}`)
    enterFunction(expr, s, key)
  }

  // A URL key takes a STRING LITERAL with a safe scheme, or an address whose
  // LEFTMOST part is a same-origin path literal and the rest an expression —
  // never a function and never a fully computed value (it could carry
  // workspace data to another origin, or turn into javascript: at run time).
  // `null` / `undefined` literals are fine.
  const urlValueSafe = (v, key) => {
    if (!v) return false
    if (v.type === 'Literal')
      return typeof v.value === 'string' ? isSafeUrl(v.value, key) : v.value == null
    if (v.type === 'Identifier' && v.name === 'undefined') return true
    if (v.type === 'TemplateLiteral' && v.expressions.length === 0)
      return isSafeUrl(v.quasis[0].value.cooked, key)
    // The address a row map builds: a same-origin path on the left, any
    // expression after it ('/chains/chain?id=' + r.id, `/chains/chain?id=${r.id}`).
    if (v.type === 'TemplateLiteral' || (v.type === 'BinaryExpression' && v.operator === '+')) {
      const prefix = leftmostLiteral(v)
      return prefix != null && sameOriginPathPrefix(prefix)
    }
    return false
  }
  // A CSS key that may carry url(…): a literal whose every url(…) is safe, or
  // a function / ternary that returns only such literals.
  const cssUrlsSafe = (str) => {
    const re = /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi
    let m
    while ((m = re.exec(String(str)))) if (!isSafeUrl(m[2], 'href')) return false
    return true
  }
  const cssValueSafe = (v) => {
    if (!v) return false
    if (v.type === 'Literal') return typeof v.value !== 'string' || cssUrlsSafe(v.value)
    if (v.type === 'TemplateLiteral' && v.expressions.length === 0)
      return cssUrlsSafe(v.quasis[0].value.cooked)
    if (v.type === 'ConditionalExpression')
      return cssValueSafe(v.consequent) && cssValueSafe(v.alternate)
    if (v.type === 'LogicalExpression') return cssValueSafe(v.right)
    if (v.type === 'ArrowFunctionExpression' || v.type === 'FunctionExpression') {
      if (v.body.type !== 'BlockStatement') return cssValueSafe(v.body)
      const rets = []
      const findReturns = (n) => {
        if (!n || typeof n !== 'object' || isFnNode(n)) return
        if (Array.isArray(n)) return n.forEach(findReturns)
        if (n.type === 'ReturnStatement') rets.push(n.argument)
        for (const k in n) if (k !== 'type' && n[k] && typeof n[k] === 'object') findReturns(n[k])
      }
      findReturns(v.body.body)
      return rets.length > 0 && rets.every((r) => cssValueSafe(r))
    }
    return false
  }

  // An object that renders as Svg / Icon: its `src` is MARKUP (Svg.js puts it
  // in the svg's innerHTML / the sprite), so it may only be an icon NAME.
  const SVG_BASES = new Set(['Svg', 'Icon'])
  const isSvgLike = (obj, ownKey) => {
    if (ownKey && SVG_BASES.has(String(ownKey).split('_')[0])) return true
    for (const p of (obj && obj.properties) || []) {
      if (p.type !== 'Property' || propName(p) !== 'extends') continue
      const vals = p.value.type === 'ArrayExpression' ? p.value.elements : [p.value]
      for (const v of vals) {
        const lit = literalString(v)
        if (lit && SVG_BASES.has(lit.split('>')[0].trim())) return true
      }
    }
    return false
  }

  // DOMQL string props: a def is data the framework turns into DOM.
  const checkDomqlProp = (key, value, node, svgLike) => {
    if (MARKUP_KEYS.has(key)) flag('markup-key', node, key)
    if (key === 'tag') {
      const t = literalString(value)
      if (t == null || !TAGS.has(t.toLowerCase()))
        flag('tag-not-allowed', node, t == null ? 'computed tag' : t)
    }
    // `on<name>` (onClick, onclick); `on` alone is a fetch trigger ('submit').
    if (
      /^on[a-zA-Z]/.test(key) &&
      value &&
      (value.type === 'Literal' || value.type === 'TemplateLiteral')
    ) {
      // a handler is a FUNCTION; a string on an on* key is inline code
      flag('string-handler', node, key)
    }
    const strVal = literalString(value)
    // a function-like string on a URL / CSS key is revived into a function
    if (
      (URL_KEYS.has(key) || CSS_URL_KEYS.has(key)) &&
      strVal != null &&
      looksLikeFunctionText(strVal)
    ) {
      flag('url-dynamic', node, key)
      return
    }
    // markup in a URL value (Svg / Icon src lands in innerHTML)
    if (URL_KEYS.has(key) && strVal != null && strVal.includes('<')) flag('url-markup', node, key)
    if (key === 'src' && svgLike && !(strVal != null && /^[\w-]+$/.test(strVal)))
      flag('svg-src', node, 'src')
    // `state:` as a string is a PATH the framework resolves against the live
    // tree ('~/<key>' = the root, '../<key>' = up, '<key>' = the parent's)
    if (
      key === 'state' &&
      value &&
      (value.type === 'Literal' || value.type === 'TemplateLiteral')
    ) {
      flag('state-string', node, strVal == null ? '(template)' : strVal)
    }
    if (
      key === 'children' &&
      value &&
      (value.type === 'Literal' || value.type === 'TemplateLiteral')
    ) {
      flag('state-string', node, 'children: ' + (strVal == null ? '(template)' : strVal))
    }
    // a fetch target: never a path, never a denied root key
    if (key === 'as' && strVal != null) {
      const bare = strVal.replace(/^\s+/, '')
      if (/^(~\/|\.\.\/|\/)/.test(bare)) flag('fetch-as', node, strVal)
      else if (ROOT_DENY.has(bare) || ROOT_DENY_RE.test(bare)) flag('fetch-as', node, strVal)
    }
    if (CSS_URL_KEYS.has(key) && !cssValueSafe(value)) flag('css-url', node, key)
    if (URL_KEYS.has(key) && !urlValueSafe(value, key)) {
      const lit = value && value.type === 'Literal' && typeof value.value === 'string'
      flag(lit ? 'url-scheme' : 'url-dynamic', node, key)
    }
  }

  const visit = (node, scope, ctx = {}) => {
    if (!node || typeof node !== 'object') return
    if (Array.isArray(node)) return node.forEach((n) => visit(n, scope))
    switch (node.type) {
      case 'ArrayExpression':
        return node.elements.forEach((n) =>
          visit(n, scope, { plainFns: !!ctx.plainFns || PLAIN_FN_KEYS.has(ctx.key) })
        )
      case 'Program':
        declareVars(scope, node.body)
        declareBlock(scope, node.body)
        return node.body.forEach((n) => visit(n, scope))
      case 'Identifier':
        return checkIdentifierRef(node, scope, ctx.position || 'value')
      case 'ThisExpression':
        if (ctx.position !== 'member-object' && ctx.position !== 'allowed-arg')
          flag('handle-escape', node, 'this')
        return
      case 'Literal':
        if (typeof node.value === 'string') {
          if (BLOCKED_STRINGS.has(node.value)) flag('blocked-string', node, node.value)
          else if (looksLikeFunctionText(node.value)) auditStringFunction(node.value, node, ctx.key)
        }
        if (node.regex) return
        return
      case 'TemplateLiteral':
        for (const q of node.quasis)
          if (BLOCKED_STRINGS.has(q.value.cooked)) flag('blocked-string', q, q.value.cooked)
        return node.expressions.forEach((e) => visit(e, scope))
      case 'TaggedTemplateExpression':
        flag('tagged-template', node, 'tagged template')
        return visit(node.quasi, scope)
      case 'WithStatement':
        flag('with', node, 'with')
        return
      case 'ImportExpression':
        flag('import', node, 'import()')
        return
      case 'MetaProperty':
        flag('meta-property', node, `${node.meta.name}.${node.property.name}`)
        return
      case 'ChainExpression':
        return visit(node.expression, scope, ctx)
      case 'MemberExpression':
        checkChain(node, scope, { isWrite: !!ctx.isWrite })
        return checkHandleValueEnd(node, scope)
      case 'CallExpression': {
        const callee = node.callee.type === 'ChainExpression' ? node.callee.expression : node.callee
        if (
          callee.type === 'MemberExpression' &&
          memberName(callee) === 'call' &&
          !callee.computed
        ) {
          const name = literalString(node.arguments[0])
          if (name == null) flag('call-name', node, 'el.call with a name that is not a literal')
          else if (!allowSet.has(name)) flag('call-not-allowed', node, name)
          checkChain(callee, scope, { isCallee: true })
          // no function may ride into a shell function as an argument
          const hasFn = (n) =>
            !!n &&
            typeof n === 'object' &&
            (isFnNode(n) ||
              Object.keys(n).some(
                (k) =>
                  k !== 'type' &&
                  n[k] &&
                  typeof n[k] === 'object' &&
                  (Array.isArray(n[k]) ? n[k].some(hasFn) : hasFn(n[k]))
              ))
          if (node.arguments.slice(1).some(hasFn)) flag('call-arg-function', node, name || 'call')
          return node.arguments
            .slice(1)
            .forEach((a) =>
              a.type === 'Identifier' || a.type === 'ThisExpression'
                ? visit(a, scope, { position: 'allowed-arg' })
                : visit(a, scope)
            )
        }
        if (
          callee.type === 'Identifier' &&
          (callee.name === 'setTimeout' || callee.name === 'setInterval')
        ) {
          if (!isFnNode(node.arguments[0])) flag('timer-string', node, callee.name)
        }
        if (callee.type === 'MemberExpression') checkChain(callee, scope, { isCallee: true })
        else visit(callee, scope)
        return node.arguments.forEach((a) => visit(a, scope))
      }
      case 'NewExpression':
        visit(node.callee, scope)
        return node.arguments.forEach((a) => visit(a, scope))
      case 'AssignmentExpression':
        if (node.left.type === 'MemberExpression') checkChain(node.left, scope, { isWrite: true })
        else if (node.left.type === 'Identifier') checkIdentifierRef(node.left, scope, 'value')
        else checkPatternKeys(node.left, scope, null)
        return visit(node.right, scope)
      case 'UpdateExpression':
        if (node.argument.type === 'MemberExpression')
          return checkChain(node.argument, scope, { isWrite: true })
        return visit(node.argument, scope)
      case 'UnaryExpression':
        if (node.operator === 'delete' && node.argument.type === 'MemberExpression') {
          return checkChain(node.argument, scope, { isWrite: true })
        }
        return visit(node.argument, scope)
      case 'ObjectExpression': {
        const plainFns = !!ctx.plainFns || PLAIN_FN_KEYS.has(ctx.key)
        const svgLike = isSvgLike(node, ctx.key)
        for (const p of node.properties) {
          if (p.type === 'SpreadElement') {
            visit(p.argument, scope)
            continue
          }
          const key = propName(p)
          if (!p.computed && key === '__proto__') flag('proto-key', p, '__proto__')
          if (p.kind === 'get' || p.kind === 'set') flag('accessor', p, `${p.kind} ${key}`)
          if (key != null) checkDomqlProp(key, p.value, p, svgLike)
          if (key === 'extends' || key === 'childExtends') checkExtendsValue(p.value, p)
          if (key != null && !p.computed && ctx.key !== 'components') checkBareKey(key, p)
          if (p.computed) {
            if (literalString(p.key) == null)
              flag('computed-key', p, 'an object key computed from an expression')
            visit(p.key, scope)
          }
          if (
            key != null &&
            (BLOCKED_MEMBERS.has(key) ||
              BLOCKED_STRINGS.has(key) ||
              key.startsWith(appRules.GENERATED_APP_BLOCKED_PREFIX)) &&
            key !== '__proto__'
          ) {
            flag('blocked-key', p, key)
          }
          // attr: {} — the element's raw attributes: no on* attribute at all
          if (ctx.key === 'attr' && key != null && /^on/i.test(key)) flag('attr-handler', p, key)
          // on: { click: … } (the v2 event map) — every entry a function
          if (ctx.key === 'on' && !isFnNode(p.value)) flag('string-handler', p, `on.${key}`)
          if (isFnNode(p.value)) enterFunction(p.value, scope, plainFns ? null : key)
          else visit(p.value, scope, { key, plainFns })
        }
        return
      }
      case 'ArrowFunctionExpression':
      case 'FunctionExpression':
        return enterFunction(node, scope, ctx.plainFns ? null : ctx.key)
      case 'FunctionDeclaration':
        return enterFunction(node, scope, null)
      case 'ClassDeclaration':
      case 'ClassExpression':
        if (node.superClass) visit(node.superClass, scope)
        for (const m of node.body.body) {
          if (m.kind === 'get' || m.kind === 'set') flag('accessor', m, m.kind)
          if (m.computed) visit(m.key, scope)
          if (m.value && isFnNode(m.value)) enterFunction(m.value, scope, null)
          else if (m.value) visit(m.value, scope)
        }
        return
      case 'VariableDeclaration':
        for (const d of node.declarations) {
          if (d.id.type !== 'Identifier') checkPatternKeys(d.id, scope, null)
          if (d.init) visit(d.init, scope)
        }
        return
      case 'BlockStatement':
      case 'StaticBlock': {
        const s = newScope(scope)
        declareBlock(s, node.body)
        return node.body.forEach((n) => visit(n, s))
      }
      case 'ForStatement':
      case 'ForInStatement':
      case 'ForOfStatement': {
        const s = newScope(scope)
        const decl = node.init || node.left
        if (decl && decl.type === 'VariableDeclaration' && decl.kind !== 'var') {
          decl.declarations.forEach((d) =>
            patternNames(d.id).forEach((n) => s.names.set(n, 'plain'))
          )
        }
        if (node.type === 'ForStatement') {
          visit(node.init, s)
          visit(node.test, s)
          visit(node.update, s)
        } else {
          if (node.left.type === 'MemberExpression') checkChain(node.left, s, { isWrite: true })
          else visit(node.left, s)
          visit(node.right, s)
        }
        return visit(node.body, s)
      }
      case 'SwitchStatement': {
        visit(node.discriminant, scope)
        const s = newScope(scope)
        node.cases.forEach((c) => declareBlock(s, c.consequent))
        return node.cases.forEach((c) => {
          visit(c.test, s)
          visit(c.consequent, s)
        })
      }
      case 'CatchClause': {
        const s = newScope(scope)
        if (node.param) {
          patternNames(node.param).forEach((n) => s.names.set(n, 'plain'))
          checkPatternKeys(node.param, s, null)
        }
        return visit(node.body, s)
      }
      case 'Property':
        // object patterns reach here only through checkPatternKeys
        return
      case 'LabeledStatement':
        return visit(node.body, scope)
      case 'BreakStatement':
      case 'ContinueStatement':
        return
      default:
        for (const k in node) {
          if (k === 'type' || k === 'start' || k === 'end') continue
          const v = node[k]
          if (v && typeof v === 'object') visit(v, scope)
        }
    }
  }

  visit(ast, newScope(null))
  return { ok: violations.length === 0, violations }
}

// The error the freestyle parser hook throws: a containment refusal is not a
// syntax error — viewers show the blocked-feature state for it.
export const generatedAppBlockedError = (violations) => {
  const err = new Error('this app uses a blocked feature')
  err.code = GENERATED_APP_BLOCKED
  err.violations = violations
  return err
}

// The audit hook handed to parseFreestyleSource: runs on the exact expression
// text the parser is about to evaluate, throws on any violation.
export const generatedAppAuditHook =
  (allow, rootKeys = [], registeredComponents = null) =>
  (expressionText) => {
    const res = auditGeneratedAppExpression(expressionText, {
      allow,
      rootKeys,
      registeredComponents
    })
    if (!res.ok) throw generatedAppBlockedError(res.violations)
  }
